import {
  BlockComponent,
  BlockViewExtension,
  ConfigExtensionFactory,
  FlavourExtension,
  toGfxBlockComponent,
} from '@blocksuite/std';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import type { ExtensionType } from '@blocksuite/store';
import { html, nothing, type PropertyValues } from 'lit';
import { literal } from 'lit/static-html.js';

import {
  type AppScreenBlockModel,
  AppScreenBlockSchema,
} from '../schemas/app-screen-model.js';

/** Shared native Edgeless view. Hosts supply a stable live DOM node, so moving
 * a screen never reloads its iframe or recreates its application state. */

export const AppScreenBlockConfigExtension = ConfigExtensionFactory<{
  content: (screen: AppScreenBlockModel) => HTMLElement | null;
  mounted: (screen: AppScreenBlockModel) => void;
}>('affine:app-screen');

class AppScreenBlockComponent extends BlockComponent<AppScreenBlockModel> {
  override renderBlock() {
    return nothing;
  }
}
export class AppScreenCanvasBlockComponent extends toGfxBlockComponent(
  AppScreenBlockComponent
) {
  override renderGfxBlock() {
    // Keep geometry measurable before the next layout reveals this screen.
    // The inert transparent block remains connected for background discovery.
    this.style.opacity = this.model.props.visible ? '' : '0';
    this.inert = !this.model.props.visible;
    return html`<div data-app-screen-content></div>`;
  }
  override updated(changed: PropertyValues) {
    super.updated(changed);
    const config = this.std.get(AppScreenBlockConfigExtension.identifier);
    const content = config.content(this.model);
    if (!content)
      throw new Error('App screen has no host content: ' + this.model.id);
    const container = this.renderRoot.querySelector(
      '[data-app-screen-content]'
    );
    if (!container)
      throw new Error('App screen has no content container: ' + this.model.id);
    if (content.parentElement !== container) container.append(content);
    config.mounted(this.model);
  }
}
export const AppScreenBlockInteraction =
  GfxViewInteractionExtension<AppScreenCanvasBlockComponent>(
    AppScreenBlockSchema.model.flavour,
    {
      resizeConstraint: { allowedHandlers: [] },
      handleSelection: () => ({ selectable: () => false }),
      handleRotate: () => ({
        beforeRotate(context) {
          context.set({ rotatable: false });
        },
      }),
    }
  );

/** The small Flow host and the full workspace share the same registration. */
export function registerAppScreenCanvasBlock() {
  if (!customElements.get('affine-app-screen-canvas'))
    customElements.define(
      'affine-app-screen-canvas',
      AppScreenCanvasBlockComponent
    );
}

export function registerAppScreenViewing(
  context: { register(extensions: ExtensionType | ExtensionType[]): unknown },
  edgeless: boolean
) {
  context.register([
    FlavourExtension(AppScreenBlockSchema.model.flavour),
    BlockViewExtension(
      AppScreenBlockSchema.model.flavour,
      literal`affine-app-screen-canvas`
    ),
  ]);
  if (edgeless) context.register(AppScreenBlockInteraction);
}
